import EmblaCarousel from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import {
  addPrevNextBtnsClickHandlers,
  addTogglePrevNextBtnsActive,
  addDotBtnsAndClickHandlers,
  addThumbBtnsClickHandlers,
  addToggleThumbBtnsActive,
  setupProgressBar,
} from "./embla-extras";

class EmblaComponent extends HTMLElement {
  constructor() {
    super();
    this.emblaApi;
    this.emblaApiThumbs;
    this.emblaNode = this.querySelector(".embla");
    this.emblaThumbs = this.querySelector(".embla-thumbs");
    this.viewportNode = this.querySelector(".embla__viewport");
    this.viewportNodeThumbs = this.querySelector(".embla-thumbs__viewport");
    this.prevBtn = this.querySelector(".embla__button--prev");
    this.nextBtn = this.querySelector(".embla__button--next");
    this.dotsNode = this.querySelector(".embla__dots");
    this.options = this.returnConfig();
    this.optionsThumbs = this.returnThumbsConfig();
    this.activeSlide = this.querySelector(".embla__slide");
    this.progressNode = this.querySelector(".embla__progress__bar");

    let slides = this.querySelectorAll(".embla__slide");

    slides.forEach((slide) => {
      slide.addEventListener("click", (e) => {
        let idx = e.target
          .closest(".embla__slide")
          .getAttribute("data-slide-index");
        this.killOpenInfo(e.target.closest(".embla__slide"));
        slide.classList.toggle("step-active");
        this.emblaApi.scrollTo(idx, false);
      });
    });

    document.addEventListener("DOMContentLoaded", (e) => {
      this.extraSlideEls = this.querySelector("embla-extra-slide-els");
      this.initEmbla();
      this.emblaApi.on("select", () => this.handleSelect());
      if (this.progressNode) {
        this.emblaApi.on("init", () => this.applyProgress());
        this.emblaApi.on("reInit", () => this.applyProgress());
        this.emblaApi.on("scroll", () => this.applyProgress());
        this.emblaApi.on("destor", () => this.removeProgress());
      }
      this.handleActiveSlide();
    });
  }

  killOpenInfo(c) {
    // TODO: Check this as it's showing an occasional error in hotjar
    let slides = this.querySelectorAll(`.embla__slide:not(#${c.id})`);
    slides.forEach((slide) => {
      slide.classList.remove("step-active");
    });
  }

  initEmbla() {
    this.emblaSlides = this.querySelectorAll(".embla__slide");
    this.emblaApi = EmblaCarousel(
      this.viewportNode,
      this.options,
      this.emblaNode.getAttribute("data-autoplay") === "true"
        ? [Autoplay(this.returnAutoplayConfig())]
        : null,
    );
    if (this.viewportNodeThumbs)
      this.emblaApiThumbs = EmblaCarousel(
        this.viewportNodeThumbs,
        this.optionsThumbs,
      );

    if (this.prevBtn && this.nextBtn) {
      addPrevNextBtnsClickHandlers(this.emblaApi, this.prevBtn, this.nextBtn);
      addTogglePrevNextBtnsActive(this.emblaApi, this.prevBtn, this.nextBtn);
    }

    if (this.dotsNode) {
      addDotBtnsAndClickHandlers(this.emblaApi, this.dotsNode);
    }

    if (this.progressNode) {
      const {applyProgress, removeProgress} = setupProgressBar(
        this.emblaApi,
        this.progressNode,
      );
      this.applyProgress = applyProgress;
      this.removeProgress = removeProgress;
    }

    if (this.emblaApiThumbs) {
      addThumbBtnsClickHandlers(this.emblaApi, this.emblaApiThumbs);

      addToggleThumbBtnsActive(this.emblaApi, this.emblaApiThumbs);

      if (
        this.emblaThumbs.getAttribute("data-change-slides-on-hover") === "true"
      ) {
        const thumbSlides = this.querySelectorAll(".embla-thumbs__slide");
        if (thumbSlides.length > 0) {
          thumbSlides.forEach((slide) => {
            slide.addEventListener("mouseenter", (e) => {
              if (this.emblaSlides.length > 1) {
                this.emblaApi.scrollTo(+slide.getAttribute("data-thumb-index"));
              } else {
                // if only one slide exists, run custom logic to handle extra elements/thumbnails
                this.extraSlideEls.showExtraSlideEl(
                  slide.getAttribute("data-thumb-index"),
                );
                thumbSlides.forEach((slide) =>
                  slide.classList.remove("embla-thumbs__slide--selected"),
                );
                slide.classList.add("embla-thumbs__slide--selected");
              }
            });
          });
          // if only one slide exists, run custom logic to handle click on extra elements/thumbnails
          if (this.emblaSlides.length < 2) {
            thumbSlides.forEach((slide) => {
              slide.addEventListener("click", (e) => {
                this.extraSlideEls.showExtraSlideEl(
                  slide.getAttribute("data-thumb-index"),
                );
                thumbSlides.forEach((slide) =>
                  slide.classList.remove("embla-thumbs__slide--selected"),
                );
                slide.classList.add("embla-thumbs__slide--selected");
              });
            });
          }
        }
      }
    }
  }

  returnConfig() {
    const isDesktop = window.innerWidth >= 990;
    const disableDragOnDesktop =
      this.emblaNode.getAttribute("data-disable-drag-desktop") === "true";
    const disableContainScroll =
      this.emblaNode.getAttribute("data-contain-scroll") === "false";

    return {
      breakpoints: {
        "(max-width: 1200px)": {
          slidesToScroll:
            +this.emblaNode.getAttribute("data-slides-to-scroll-tablet") ||
            "auto",
        },
        "(max-width: 1024px)": {
          slidesToScroll:
            +this.emblaNode.getAttribute("data-slides-to-scroll-mobile") ||
            "auto",
          align: this.emblaNode.getAttribute("data-align-mobile") || "center",
        },
      },
      align: this.emblaNode.getAttribute("data-align") || "center",
      loop: this.emblaNode.getAttribute("data-loop") === "true" ? true : false,
      slidesToScroll:
        +this.emblaNode.getAttribute("data-slides-to-scroll") || "auto",
      startIndex: +this.emblaNode.getAttribute("data-start-index") || 0,
      containScroll: disableContainScroll ? false : "trimSnaps",
      dragFree:
        this.emblaNode.getAttribute("data-drag-free") === "true" ? true : false,
      axis: this.emblaNode.getAttribute("data-axis") || "x",
      duration: +this.emblaNode.getAttribute("data-duration") || 25,
      slides:
        this.emblaNode.getAttribute("data-custom-class") || ".embla__slide",
      inViewThreshold: 1,
      watchDrag: isDesktop && disableDragOnDesktop ? false : true,
    };
  }

  returnAutoplayConfig() {
    return {
      delay: +this.emblaNode.getAttribute("data-autoplay-speed") || 4000,
    };
  }

  returnThumbsConfig() {
    return {
      containScroll: "keepSnaps",
      dragFree: true,
      loop:
        this.emblaThumbs?.getAttribute("data-thumbs-loop") === "true"
          ? true
          : false,
      loop: true,
    };
  }

  handleSelect() {
    const activeThumb = this.querySelector(".embla-thumbs__slide--selected");
    if (!activeThumb) return;

    const activeThumbIndex = activeThumb.getAttribute("data-thumb-index");

    this.handleActiveSlide(
      this.querySelector(
        `.embla__slide[data-slide-index="${activeThumbIndex}"]`,
      ),
    );
    if (this.extraSlideEls)
      this.extraSlideEls.showExtraSlideEl(activeThumbIndex);
  }

  handleActiveSlide(newSlide) {
    if (newSlide) {
      this.activeSlide.classList.remove("embla__slide--active");
      this.activeSlide = newSlide;
      this.activeSlide.classList.add("embla__slide--active");
    } else {
      this.activeSlide.classList.add("embla__slide--active");
    }
  }

  goToStart() {
    this.emblaApi.scrollTo(0);
  }

  restart() {
    this.initEmbla();
  }
}

customElements.define("embla-component", EmblaComponent);
